import { ReactNode, MouseEventHandler } from "react";

import { css } from "@compiled/react";

import CheckIcon from "@atlaskit/icon/glyph/check";
import { media } from "@atlaskit/primitives";
import { N0 } from "@atlaskit/theme/colors";

import Link from "@/components/wpl/link/src";

const addonContainerCss = css({
    padding: 32,
    display: "flex",
    flex: "1 1 0",
    flexDirection: "column",
    borderRadius: 20,
    background: N0,
    boxShadow: `0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 1px 0px rgba(9, 30, 66, 0.25);`,
    gap: 8,
    width: "100%",
    maxWidth: 608,

    [media.above.lg]: {
        flexDirection: "row",
        gap: 32
    }
});

const sectionLeftCss = css({
    display: "flex",
    flexDirection: "column"
});

const sectionRightCss = css({
    display: "none",

    [media.above.lg]: {
        display: "flex",
        alignItems: "center",
        maxWidth: 504
    }
});

const headingTextCss = css({
    fontSize: 22,
    fontWeight: 400,
    lineHeight: "28px",
    margin: 0,
    paddingBlockStart: 21,
    position: "relative"
});

const bodyTextCss = css({
    margin: 0,
    paddingBlockStart: 4
});

const featureListCss = css({
    listStyleType: "none",
    padding: 0,
    marginBlockStart: 21,
    marginBlockEnd: 24
});

const featureListItemCss = css({
    display: "flex",
    alignItems: "start",
    gap: 8,
    paddingBlockStart: 4
});

interface LearnMore {
    text: string;
    href: string;
    onClick?: MouseEventHandler;
}

export interface AddonProps {
    logo: ReactNode;
    heading: ReactNode;
    subheading: ReactNode;
    features: string[];
    learnMore?: LearnMore;
    imgSrc?: string;
    imgAlt?: string;
}

export const Addon = ({
    logo,
    heading,
    subheading,
    features,
    learnMore,
    imgSrc,
    imgAlt
}: AddonProps) => {
    return (
        <div css={addonContainerCss}>
            <div css={sectionLeftCss}>
                <span>{logo}</span>
                <h4 css={headingTextCss}>{heading}</h4>

                <p css={bodyTextCss}>{subheading}</p>

                <ul css={featureListCss}>
                    {features.map((feature, index) => (
                        <li key={index} css={featureListItemCss}>
                            <CheckIcon
                                size="medium"
                                label=""
                                primaryColor="#44546F"
                            />
                            <span>{feature}</span>
                        </li>
                    ))}
                </ul>

                {learnMore && (
                    <Link
                        text={learnMore.text}
                        doesArrowAnimate={true}
                        type="basic-underline"
                        href={learnMore.href}
                        onClick={learnMore.onClick}
                    />
                )}
            </div>
            {imgSrc && (
                <div css={sectionRightCss}>
                    <img src={imgSrc} alt={imgAlt} height={268} width={504} />
                </div>
            )}
        </div>
    );
};

export const learnMoreEventData = {
    eventName: "pricingAddonLearnMoreClicked",
    eventType: "ui",
    eventComponent: "linkButton",
    event: "clicked",
    actionSubject: "linkButton",
    actionSubjectId: "pricingAddonLearnMore",
    action: "clicked"
};
